import { Scroller } from '@theme/scrolling';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let railOverflowGroup;

// Ordinary horizontal rails only write overflow/tab state. One observer can
// measure its changed rails together before any of those writes. Comparison
// columns retain their observer because resetting their drag/snap state also
// invokes Scroller's geometry-dependent methods.
function observeRailOverflow(track, read, apply, signal) {
  if (!railOverflowGroup) {
    const registrations = new Map();
    const observer = new ResizeObserver((entries) => {
      const pending = new Set(entries.map(({ target }) => registrations.get(target))
        .filter((registration) => registration && !registration.signal.aborted));
      const measurements = [...pending].map((registration) => [registration, registration.read()]);
      for (const [registration, state] of measurements) {
        if (!registration.signal.aborted) registration.apply(state);
      }
    });
    railOverflowGroup = { registrations, observer };
  }

  const group = railOverflowGroup;
  const registration = { read, apply, signal };
  group.registrations.set(track, registration);
  group.observer.observe(track);
  return {
    disconnect() {
      if (group.registrations.get(track) !== registration) return;
      group.registrations.delete(track);
      group.observer.unobserve(track);
      if (group.registrations.size) return;
      group.observer.disconnect();
      if (railOverflowGroup === group) railOverflowGroup = undefined;
    },
  };
}

class ThemeScroller extends HTMLElement {
  #dragged = false;

  /** Lets capture-phase actions distinguish a drag release from an image click. */
  isDragClick(event) {
    return this.#dragged && event.detail !== 0;
  }

  connectedCallback() {
    const track = this.querySelector('[data-theme-scroll]');
    if (!track) return;
    this.controller?.abort();
    this.scroller?.destroy();
    this.resizeObserver?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const snap = track.hasAttribute('data-theme-snap');
    const columns = this.hasAttribute('data-theme-columns');
    const previous = this.querySelector('[data-theme-prev]');
    const next = this.querySelector('[data-theme-next]');
    const controls = this.querySelector('[data-theme-scroll-controls]');
    let overflowing = true;
    let destination;
    const readControls = () => ({ left: track.scrollLeft, max: track.scrollWidth - track.clientWidth });
    const applyControls = ({ left, max }) => {
      const atStart = !overflowing || left <= 1;
      const atEnd = !overflowing || left >= max - 1;
      if (previous && previous.disabled !== atStart) previous.disabled = atStart;
      if (next && next.disabled !== atEnd) next.disabled = atEnd;
    };
    const updateControls = () => { if (columns) applyControls(readControls()); };
    // Native scroll events below also cover programmatic moves. Registering the
    // same controls callback with Scroller would measure twice on user scrolls.
    const scroller = snap ? new Scroller(track, { axis: 'x', onScroll: () => {} }) : null;
    this.scroller = scroller;
    let snapping;
    const setSnap = (value) => {
      // Scroller's setter measures its axis and writes scroll-snap-type.
      // Repeating the same state needlessly dirties the next geometry read.
      if (snapping === value) return;
      scroller.snap = value;
      snapping = value;
    };
    let drag;
    this.#dragged = false;
    let settling = 0;
    const positions = () => {
      const start = track.getBoundingClientRect().left + track.clientLeft;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const left = track.scrollLeft;
      const items = columns ? track.querySelectorAll('[data-theme-scroll-item]') : track.children;
      return [...new Set(Array.from(items, (card) =>
        Math.max(0, Math.min(max, card.getBoundingClientRect().left - start + left))
      ))];
    };
    const nearest = (points, value) => points.reduce((best, point) =>
      Math.abs(point - value) < Math.abs(best - value) ? point : best, points[0] ?? 0);
    const settle = async (left) => {
      const current = ++settling;
      destination = left;
      setSnap(false);
      scroller.to(left, { instant: reduceMotion.matches });
      await scroller.finished;
      if (!signal.aborted && !drag && current === settling) {
        destination = undefined;
        const state = columns ? readControls() : null;
        setSnap(!columns || overflowing);
        if (state) applyControls(state);
      }
    };
    if (snap) {
      const applyTrackOverflow = (overflow) => {
        if (track.hasAttribute('data-theme-overflow') !== overflow) track.toggleAttribute('data-theme-overflow', overflow);
        const tabIndex = overflow ? 0 : -1;
        if (track.tabIndex !== tabIndex) track.tabIndex = tabIndex;
      };
      const updateOverflow = () => {
        if (signal.aborted) return;
        // The comparison table includes a trailing cell-padding gutter.
        const gutter = columns ? Number(track.dataset.themeScrollGutter) || 0 : 0;
        const width = track.scrollWidth;
        const client = track.clientWidth;
        const state = columns ? { left: track.scrollLeft, max: width - client } : null;
        const overflow = width > client + gutter + 1;
        overflowing = overflow;
        if (columns) {
          if (!overflow) {
            ++settling;
            destination = undefined;
            const pointer = drag?.id;
            drag = undefined;
            if (pointer !== undefined && track.hasPointerCapture(pointer)) track.releasePointerCapture(pointer);
            scroller.to(0, { instant: true });
            track.classList.remove('is-dragging');
          }
          setSnap(overflow && !drag);
          if (this.hasAttribute('data-theme-overflow') !== overflow) this.toggleAttribute('data-theme-overflow', overflow);
          if (controls && controls.hidden !== !overflow) controls.hidden = !overflow;
          applyControls(state);
        }
        applyTrackOverflow(overflow);
      };
      if (columns) {
        this.resizeObserver = new ResizeObserver(updateOverflow);
        this.resizeObserver.observe(track);
        for (const child of track.children) this.resizeObserver.observe(child);
        track.addEventListener('scroll', updateControls, { signal, passive: true });
      } else {
        this.resizeObserver = observeRailOverflow(track,
          () => track.scrollWidth > track.clientWidth + 1,
          applyTrackOverflow, signal);
      }
      // The initial observer delivery measures overflow after layout, avoiding
      // a duplicate synchronous measurement while custom elements connect.
      track.addEventListener('keydown', (event) => {
        if ((columns && !overflowing) || event.target !== track || event.altKey || event.ctrlKey || event.metaKey) return;
        const points = positions();
        const left = destination ?? track.scrollLeft;
        let target;
        if (event.key === 'ArrowRight') target = points.find((point) => point > left + 1) ?? points.at(-1);
        else if (event.key === 'ArrowLeft') target = points.findLast((point) => point < left - 1) ?? 0;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = points.at(-1);
        else return;
        event.preventDefault();
        settle(target ?? 0);
      }, { signal });
    }
    track.addEventListener('pointerdown', (event) => {
      if (columns && !overflowing) return;
      if (event.pointerType !== 'mouse' || event.button !== 0 || track.scrollWidth <= track.clientWidth) return;
      if (event.target.closest('input, select, textarea') || (event.target.closest('button') && !track.hasAttribute('data-theme-drag-buttons'))) return;
      destination = undefined;
      drag = { id: event.pointerId, x: event.clientX, scroll: track.scrollLeft };
      this.#dragged = false;
      if (snap) {
        ++settling;
        setSnap(false);
        scroller.to(track.scrollLeft, { instant: true });
      }
    }, { signal });
    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const distance = event.clientX - drag.x;
      if (!this.#dragged && Math.abs(distance) < 5) return;
      this.#dragged = true;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
      if (snap) scroller.to(drag.scroll - distance, { instant: true });
      else track.scrollLeft = drag.scroll - distance;
      event.preventDefault();
    }, { signal });
    const endDrag = (event) => {
      if (!drag || (event.type !== 'blur' && event.pointerId !== drag.id)) return;
      const previous = drag;
      drag = undefined;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(previous.id)) track.releasePointerCapture(previous.id);
      if (snap) {
        const points = positions();
        const left = track.scrollLeft;
        let target = nearest(points, left);
        const start = nearest(points, previous.scroll);
        const distance = left - previous.scroll;
        // A deliberate short drag still advances a card; tiny movements stay put.
        if (event.type === 'pointerup' && target === start && Math.abs(distance) > 24) {
          target = distance > 0
            ? points.find((point) => point > start + 1) ?? target
            : points.findLast((point) => point < start - 1) ?? target;
        }
        settle(target);
      }
    };
    window.addEventListener('pointerup', endDrag, { signal });
    window.addEventListener('pointercancel', endDrag, { signal });
    window.addEventListener('blur', endDrag, { signal });
    track.addEventListener('lostpointercapture', endDrag, { signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    track.addEventListener('click', (event) => {
      if (!this.isDragClick(event)) return;
      event.preventDefault();
      event.stopPropagation();
      this.#dragged = false;
    }, { signal, capture: true });
    const move = (direction) => {
      if (columns) {
        if (!overflowing) return;
        const points = positions();
        const left = destination ?? track.scrollLeft;
        const target = direction > 0
          ? points.find((point) => point > left + 1) ?? points.at(-1)
          : points.findLast((point) => point < left - 1) ?? 0;
        settle(target ?? 0);
      } else {
        track.scrollBy({ left: direction * track.clientWidth * 0.72, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    };
    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
  }

  disconnectedCallback() {
    this.#dragged = false;
    this.controller?.abort();
    this.scroller?.destroy();
    this.resizeObserver?.disconnect();
    const track = this.querySelector('[data-theme-scroll]');
    track?.classList.remove('is-dragging');
    if (track?.hasAttribute('data-theme-snap')) track.style.removeProperty('scroll-snap-type');
    if (this.hasAttribute('data-theme-columns')) {
      this.removeAttribute('data-theme-overflow');
      track?.removeAttribute('data-theme-overflow');
      const controls = this.querySelector('[data-theme-scroll-controls]');
      if (controls) controls.hidden = true;
    }
  }
}

if (!customElements.get('theme-scroller')) {
  customElements.define('theme-scroller', ThemeScroller);
}
